/**
 * Admin Password Environment Configuration Enforcement
 *
 * Enforces that the system refuses to start or operate if the admin password
 * environment variable is missing, disallowing any default or fallback password.
 *
 * This module:
 * - Checks for the presence of the VITE_ADMIN_PASSWORD environment variable at startup
 * - Rejects missing or empty values with a critical error
 * - Rejects known insecure default passwords
 * - Logs clear error messages indicating the missing configuration
 * - Prevents application initialization if the check fails
 *
 * In a browser (Vite) environment, the admin password should be provided via
 * the VITE_ADMIN_PASSWORD environment variable (prefixed with VITE_ to be exposed
 * to the frontend by Vite).
 *
 * TODO(Migrate existing frontend environment variables to runtime config
 * format): this module still reads `import.meta.env.VITE_ADMIN_PASSWORD`
 * directly, i.e. it is still a build-time-only check — a deployment that
 * provides configuration purely via `runtime-config.json` (no `VITE_*`
 * vars baked in at build time, per docs/v2/05-runtime-configuration.md)
 * will fail this check at startup even if otherwise fully configured.
 * Migrating this would mean adding an `adminPassword` field to
 * `RuntimeConfig` (src/lib/config/runtimeConfig.ts) and validating it
 * after `ConfigProvider` loads config rather than synchronously in
 * `main.tsx`. Left as build-time-only here to avoid changing this
 * security control's behavior as a side effect of the runtime-config
 * migration; see docs/v2/05-runtime-configuration.md "Migration Status".
 */

/**
 * List of known insecure default passwords that should never be used
 */
const INSECURE_DEFAULT_PASSWORDS = [
  'admin123',
  'password',
  'default',
  'admin',
  'root',
  'test',
  'test123',
];

/**
 * Validates the admin password with a provided value (for testing purposes).
 * This is the core validation logic that can be tested independently.
 *
 * @param password - The password to validate
 * @throws {Error} If the password is missing, empty, or insecure
 */
export function validateAdminPasswordValue(password: string | undefined): void {
  // Check if the variable is missing
  if (password === undefined) {
    throw new Error(
      '[FATAL] Missing required VITE_ADMIN_PASSWORD environment variable. ' +
        'Please set it before starting the server. ' +
        'Example: VITE_ADMIN_PASSWORD=your-secure-password npm run dev'
    );
  }

  // Check if the value is empty or only whitespace
  const trimmedPassword = String(password).trim();
  if (trimmedPassword === '') {
    throw new Error(
      '[FATAL] VITE_ADMIN_PASSWORD is set to an empty string. ' +
        'Please provide a non-empty, secure admin password in your environment configuration.'
    );
  }

  // Check if the password is a known insecure default
  if (INSECURE_DEFAULT_PASSWORDS.includes(trimmedPassword.toLowerCase())) {
    throw new Error(
      '[FATAL] VITE_ADMIN_PASSWORD is set to an insecure default value. ' +
        'Please set a strong, unique admin password in your environment configuration.'
    );
  }
}

/**
 * Checks if the admin password environment variable is properly configured.
 *
 * Throws an error if:
 * - The VITE_ADMIN_PASSWORD environment variable is not set
 * - The value is an empty string or only whitespace
 * - The value is a known insecure default password
 *
 * @throws {Error} If the admin password is missing, empty, or insecure
 *
 * @example
 * ```typescript
 * try {
 *   checkAdminPassword();
 * } catch (error) {
 *   console.error('[FATAL]', error.message);
 *   process.exit(1);
 * }
 * ```
 */
export function checkAdminPassword(): void {
  // In Vite, environment variables are accessed via import.meta.env
  // and must be prefixed with VITE_ to be exposed to the frontend
  const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD;
  validateAdminPasswordValue(adminPassword);
}

/**
 * Checks admin password and exits the process if validation fails.
 *
 * This is the exported function that should be called at application startup,
 * before any authentication or server initialization logic.
 *
 * In a browser environment, this will throw an error that should be caught
 * by an error boundary or initialization handler.
 *
 * @throws {Error} If the admin password check fails
 */
export function checkAdminPasswordOrExit(): void {
  try {
    checkAdminPassword();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(message);

    // In Node.js (development server), exit the process
    if (typeof process !== 'undefined' && typeof process.exit === 'function') {
      process.exit(1);
    }

    // In browser, re-throw so it can be caught by error boundary
    throw error;
  }
}
